/**
 * Pure helpers for the AI usage admin view.
 *
 * The API returns one line per (kind, business, user, model) with tokens and
 * an estimated cost. Everything the screen shows is a regrouping of those
 * lines, done here so it can be tested without React.
 *
 * The kinds are chat, tagging, invoices and automations. Each of the last
 * three gets its own totals and company table; only chat feeds the user
 * table, since the others run under `SYSTEM:` actors rather than people.
 * The licence tab reads `chargedByCompany`, chat and scheduled tasks
 * together, because those are the rows a licence or the free-tier
 * allowance pays for.
 *
 * Cost sums distinguish "nothing priced" from "some of it unpriced": a line
 * whose model has no rate contributes `costUsd: null`, and a total built only
 * from such lines stays `null` rather than reading as a free month. A total
 * that mixes priced and unpriced lines keeps the priced sum and sets
 * `unpriced`, which the screen marks with an asterisk.
 */

export const toYearMonth = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const currentYearMonth = (now = new Date()) => toYearMonth(now);

const emptyTotals = () => ({
  turns: 0,
  modelCalls: 0,
  toolCalls: 0,
  inputTokens: 0,
  outputTokens: 0,
  cacheReadTokens: 0,
  cacheWriteTokens: 0,
  incompleteTurns: 0,
  // Null until a line carries the count: tagging from before the tagger kept
  // it, or an API build that does not send it, is unknown rather than none.
  tagsAdded: null,
  costUsd: null,
  unpriced: false,
});

export const sumLines = (lines) =>
  lines.reduce((acc, line) => {
    acc.turns += line.turns || 0;
    acc.modelCalls += line.modelCalls || 0;
    acc.toolCalls += line.toolCalls || 0;
    acc.inputTokens += line.inputTokens || 0;
    acc.outputTokens += line.outputTokens || 0;
    acc.cacheReadTokens += line.cacheReadTokens || 0;
    acc.cacheWriteTokens += line.cacheWriteTokens || 0;
    acc.incompleteTurns += line.incompleteTurns || 0;
    if (line.tagsAdded != null) {
      acc.tagsAdded = (acc.tagsAdded || 0) + line.tagsAdded;
    }
    if (line.costUsd == null) {
      acc.unpriced = true;
    } else {
      acc.costUsd = (acc.costUsd || 0) + line.costUsd;
    }
    return acc;
  }, emptyTotals());

export const totalTokens = (totals) =>
  totals.inputTokens +
  totals.outputTokens +
  totals.cacheReadTokens +
  totals.cacheWriteTokens;

const unique = (values) => Array.from(new Set(values));

const groupBy = (lines, keyOf, build) => {
  const groups = new Map();
  for (const line of lines) {
    const key = keyOf(line);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(line);
  }
  return Array.from(groups, ([key, group]) => build(key, group));
};

export const summariseReport = (report) => {
  const lines = report.lines || [];
  const businesses = report.businesses || {};
  const users = report.users || {};
  const rates = report.rates || {};

  const chat = lines.filter((line) => line.kind === 'chat');
  const tagging = lines.filter((line) => line.kind === 'tagging');
  // Invoice scanning: `turns` on these lines are scan runs, one per lambda
  // invocation per model, not invoices. An invoice that needed two models
  // counts twice.
  const invoices = lines.filter((line) => line.kind === 'invoices');
  // Scheduled tasks the assistant ran on its own: one turn per run, filed
  // under the `SYSTEM:automation` actor, so they belong in no user table.
  const automations = lines.filter((line) => line.kind === 'automations');
  // What a licence or the free-tier allowance pays for. Chat and scheduled
  // tasks are the two writers that charge a business; tagging and invoice
  // scanning are charged to nobody, so the licence figures leave them out.
  const charged = [...chat, ...automations];

  const companyRow = (key, group) => ({
    businessId: key || null,
    businessName: key ? businesses[key] : undefined,
    users: unique(group.map((line) => line.userId)).length,
    ...sumLines(group),
  });

  return {
    all: sumLines(lines),
    chat: sumLines(chat),
    tagging: sumLines(tagging),
    invoices: sumLines(invoices),
    automations: sumLines(automations),
    byCompany: groupBy(chat, (line) => line.businessId || '', companyRow),
    chargedByCompany: groupBy(
      charged,
      (line) => line.businessId || '',
      companyRow
    ),
    byUser: groupBy(
      chat,
      (line) => line.userId,
      (key, group) => {
        const businessIds = unique(
          group.map((line) => line.businessId).filter(Boolean)
        );
        return {
          userId: key,
          userName: users[key],
          businessIds,
          businessNames: businessIds.map((id) => businesses[id] || id),
          ...sumLines(group),
        };
      }
    ),
    byModel: groupBy(
      lines,
      (line) => `${line.kind}|${line.modelId}`,
      (key, group) => ({
        key,
        kind: group[0].kind,
        modelId: group[0].modelId,
        modelLabel: rates[group[0].modelId]
          ? rates[group[0].modelId].label
          : undefined,
        ...sumLines(group),
      })
    ),
    taggingByCompany: groupBy(
      tagging,
      (line) => line.businessId || '',
      companyRow
    ),
    invoicesByCompany: groupBy(
      invoices,
      (line) => line.businessId || '',
      companyRow
    ),
    automationsByCompany: groupBy(
      automations,
      (line) => line.businessId || '',
      companyRow
    ),
    unpricedModels: report.unpricedModels || [],
  };
};
